import { BellIcon, ClipboardDocumentListIcon, DocumentIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { useSelector } from "react-redux";

import { selectListOfMenuResourcesByActiveEid, selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import { useGetAccountPositionQuery } from "@/shared/api/account";
import MenuCard from "@/shared/components/MenuCard";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { CircularProgress } from "@/shared/components/ui/circular-progress";
import { Skeleton } from "@/shared/components/ui/skeleton";

const DOCUMENT_PAGES = [
  {
    titleKey: t`Activity`,
    path: "/documents/activity",
    menuAcl: "document.activity",
    icon: <ClipboardDocumentListIcon className="h-5 w-auto" />,
  },
  {
    titleKey: t`Statements`,
    path: "/documents/statement",
    menuAcl: "document.statement",
    icon: <DocumentIcon className="h-5 w-auto" />,
  },
  {
    titleKey: t`Notifications`,
    path: "/documents/notification",
    menuAcl: "document.notification",
    icon: <BellIcon className="h-5 w-auto" />,
  },
];

const getDocumentsPages = (aclMenu: string[]) => DOCUMENT_PAGES.filter((page) => aclMenu.includes(page.menuAcl));

const DocumentSkeleton = () => <Skeleton className="h-64 rounded-2xl" />;

function DocumentsLayout() {
  const eid = useSelector(selectMaybeActiveAccountEid);
  const aclMenu = useSelector(selectListOfMenuResourcesByActiveEid);
  const documentsPages = getDocumentsPages(aclMenu);
  const positionQuery = useGetAccountPositionQuery(eid || skipToken);

  return (
    <ApiErrorBoundary
      LoadingSkeleton={<DocumentSkeleton />}
      shouldRetry
      queries={[positionQuery]}
      translatedErrorMessage="Error loading documents"
      loadIndicator="isLoading"
    >
      <MenuCard menuItems={documentsPages} menuLabel={t`Select a Document Type`}>
        <div className="relative min-h-[200px] w-full p-6">
          <Suspense fallback={<CircularProgress className="absolute inset-0 m-auto" />}>
            <Outlet />
          </Suspense>
        </div>
      </MenuCard>
    </ApiErrorBoundary>
  );
}

export const Route = createFileRoute("/_authenticated/documents")({
  component: DocumentsLayout,
  context: () => ({ permissions: "document" }),
});
